import { useLayoutEffect, useRef, useState } from 'react';

interface ResultScreenProps {
  score: number;
  correctCount: number;
  totalQuestions: number;
  onRetry: () => void;
  onNext: () => void;
}

function getComment(score: number): string {
  if (score >= 90) return '기호와 숫자를 아주 빠르게 짝지었어요.';
  if (score >= 70) return '기호와 숫자를 잘 짝지었어요.';
  if (score >= 50) return '기호와 숫자를 대부분 잘 짝지었어요.';
  if (score >= 40) return '기호와 숫자를 차근차근 짝지었어요.';
  return '기호와 숫자를 짝짓는 연습을 마쳤어요.';
}

// 고양이 캐릭터 (스픽 공통 리소스 — Spatial Span 결과 화면과 동일)
const CAT_IMAGE =
  'https://storage.helloreaddy.io/project_files/a1e21e58-82d2-4e64-8a34-d104a4996fee/76888ab4-7017-4ed2-b54e-aff393facf3f_compressed_-.webp';

// 기준(scale = 1) 치수
const RING_MAX = 266;
const STROKE_MAX = 16;
const CAT_HEIGHT_MAX = 172;
const CAT_RATIO = 330 / 172;
const MIN_RING = 140;
const MIN_SCALE = 0.54;

// scale = 1 일 때 캐릭터 + 카드가 세로로 쓰는 높이(간격 포함)
const SCALABLE_HEIGHT = 575;
// 게이지 크기와 무관하게 고정으로 필요한 텍스트/배지 영역 (여유분 포함)
const FIXED_HEIGHT = 150;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getInitialAvailable(): number {
  if (typeof window === 'undefined') return 560;
  const visualViewport = window.visualViewport;
  const height = visualViewport?.height ?? window.innerHeight;
  // 상단바(56) + 하단 버튼 영역(약 130) 제외한 대략치
  return Math.max(300, Math.round(height - 186));
}

// 결과 화면 (스픽 스타일 — Spatial Span 결과 화면과 동일한 구성)
// - 고양이 캐릭터가 흰 카드 위에 올라앉은 구성
// - 화면 높이가 부족하면 캐릭터와 원형 게이지를 함께 비례 축소해 스크롤 없이 한 화면에 담는다.
// - 잘라내기(overflow hidden)로 숨기지 않고, 실제 크기를 줄여서 맞춘다.
export default function ResultScreen({
  score,
  correctCount,
  totalQuestions,
  onRetry,
  onNext,
}: ResultScreenProps) {
  const [displayed, setDisplayed] = useState(0);
  const [showComment, setShowComment] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(getInitialAvailable);
  const areaRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<number>(0);

  const comment = getComment(score);
  const reachedText =
    totalQuestions > 0
      ? `정답 ${correctCount} / ${totalQuestions}`
      : '다시 한 번 도전해볼까요?';

  // 실제 남은 높이 측정 (주소창/하단 메뉴 변화에도 다시 계산)
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;

    const update = () => {
      setAvailableHeight(Math.max(el.clientHeight, 260));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('orientationchange', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  // 점수 Count-up + 원형 게이지 채우기 (동일 진행값 공유)
  useLayoutEffect(() => {
    const duration = 1100;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(score * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplayed(score);
        setShowComment(true);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [score]);

  // 화면이 짧을수록 전체 비율을 줄여 한 화면에 맞춘다
  const scale = clamp(
    (availableHeight - FIXED_HEIGHT) / SCALABLE_HEIGHT,
    MIN_SCALE,
    1,
  );

  const ringSize = Math.max(MIN_RING, Math.round(RING_MAX * scale));
  const stroke = Math.max(10, Math.round(STROKE_MAX * scale));
  const radius = (ringSize - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  const catHeight = Math.round(CAT_HEIGHT_MAX * scale);
  const catWidth = Math.round(catHeight * CAT_RATIO);
  const catOverlap = Math.round(24 * scale);

  const cardPaddingTop = Math.max(14, Math.round(32 * scale));
  const cardPaddingBottom = Math.max(22, Math.round(56 * scale));
  const ringMarginTop = Math.max(10, Math.round(32 * scale));
  const commentMarginTop = Math.max(10, Math.round(28 * scale));

  const scoreFontSize = Math.max(48, Math.round(92 * scale));
  const unitFontSize = Math.round(18 * Math.max(scale, 0.8));
  const commentFontSize = scale < 0.8 ? 15 : 17;
  const ctaHeight = scale < 0.86 ? 52 : 58;

  const progressRatio = Math.min(Math.max(displayed / 100, 0), 1);
  const dashOffset = circumference * (1 - progressRatio);

  return (
    <div className="flex flex-col h-full bg-transparent px-5">
      {/* 캐릭터 + 결과 카드: 남은 높이 안에서 비례 축소되어 스크롤 없이 표시된다 */}
      <div
        ref={areaRef}
        className="flex-1 min-h-0 flex flex-col justify-end"
      >
        <div className="w-full flex flex-col">
          {/* 고양이 캐릭터 */}
          <div className="shrink-0 flex justify-center items-end relative z-10 animate-sh-result-cat">
            <img
              src={CAT_IMAGE}
              alt="기뻐하는 고양이 캐릭터"
              title="기호 맞추기 결과 고양이 캐릭터"
              style={{ width: `${catWidth}px`, height: `${catHeight}px` }}
              className="object-contain object-bottom"
            />
          </div>

          {/* 결과 카드 */}
          <div
            className="relative z-0 bg-white rounded-[28px] flex flex-col items-center animate-sh-result-card"
            style={{
              marginTop: -catOverlap,
              paddingTop: cardPaddingTop,
              paddingBottom: cardPaddingBottom,
              paddingLeft: 28,
              paddingRight: 28,
            }}
          >
            {/* 결과 배지 */}
            <span className="inline-flex items-center rounded-full bg-primary-50 text-primary-600 text-[14px] font-semibold px-4 py-2 whitespace-nowrap">
              기호 맞추기 결과
            </span>

            {/* 원형 게이지 */}
            <div
              className="relative shrink-0"
              style={{
                width: ringSize,
                height: ringSize,
                marginTop: ringMarginTop,
              }}
            >
              <svg
                width={ringSize}
                height={ringSize}
                viewBox={`0 0 ${ringSize} ${ringSize}`}
                className="rotate-90"
              >
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  fill="none"
                  strokeWidth={stroke}
                  className="stroke-background-500"
                />
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  fill="none"
                  strokeWidth={stroke}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className="stroke-accent-500"
                />
              </svg>

              {/* 중앙 점수 */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p
                  className="font-bold text-foreground-950 leading-none tabular-nums"
                  style={{ fontSize: scoreFontSize }}
                >
                  {displayed}
                </p>
                <p
                  className="font-medium text-foreground-500"
                  style={{ fontSize: unitFontSize, marginTop: Math.max(4, Math.round(ringSize * 0.009)) }}
                >
                  점
                </p>
              </div>
            </div>

            {/* 평가 문구 */}
            <p
              className={`
                font-bold text-foreground-950 leading-snug text-center
                transition-all duration-500
                ${showComment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
              `}
              style={{ fontSize: commentFontSize, marginTop: commentMarginTop }}
            >
              {comment}
            </p>

            {/* 보조 문구 */}
            <p
              className={`
                text-[14px] text-foreground-500 leading-snug text-center mt-2
                transition-all duration-500
                ${showComment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
              `}
            >
              {reachedText}
            </p>
          </div>
        </div>
      </div>

      {/* 하단 CTA — 다시하기(White, 좁게) / 다음(Primary, 넓게) */}
      <div className="shrink-0 flex gap-3 pt-3 pb-[calc(16px+env(safe-area-inset-bottom))]">
        <button
          onClick={onRetry}
          style={{ height: ctaHeight }}
          className="w-[37%] shrink-0 rounded-2xl bg-white text-foreground-600 font-semibold text-[16px] whitespace-nowrap
            active:scale-[0.99] transition-transform duration-150 hover:bg-background-100 cursor-pointer"
        >
          다시하기
        </button>
        <button
          onClick={onNext}
          style={{ height: ctaHeight }}
          className="flex-1 rounded-2xl sh-cta text-white font-semibold text-[16px] whitespace-nowrap
            active:scale-[0.99] transition-transform duration-150 cursor-pointer"
        >
          다음
        </button>
      </div>
    </div>
  );
}